"use client";

import { useTranslations } from "next-intl";
import * as React from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Separator } from "@/components/ui/separator";
import type { ExtendedUserStake } from "@/hooks/stake/useFetchStakeContractData";
import { useDateFormatter } from "@/lib/i18n/date";
import { formatStakeNumber } from "@/lib/stake/format-number";
import { STAKE_CONSTANTS } from "@/lib/stake/stake-constants";
import { cn } from "@/lib/utils";
import { ClaimRewardButton } from "./ClaimRewardButton";
import { WithdrawStakeButton } from "./WithdrawStakeButton";

interface StakeCardProps {
  stake: ExtendedUserStake;
}

function durationLabel(
  durationSec: bigint,
  t: ReturnType<typeof useTranslations<"stake">>,
): string {
  if (durationSec === STAKE_CONSTANTS.ONE_YEAR_SECONDS)
    return t("card.duration12");
  if (durationSec === STAKE_CONSTANTS.EIGHTEEN_MONTH_SECONDS)
    return t("card.duration18");
  if (durationSec === STAKE_CONSTANTS.TWO_YEAR_SECONDS)
    return t("card.duration24");
  return t("card.durationMonths", {
    months: (Number(durationSec) / (30 * 86400)).toFixed(0),
  });
}

export const StakeCard: React.FC<StakeCardProps> = ({ stake }) => {
  const t = useTranslations("stake");
  const tCommon = useTranslations("common");
  const [isExpanded, setIsExpanded] = React.useState(false);
  const df = useDateFormatter();

  const startDate = df.dateTime(stake.start_sec);
  const endDate = df.dateTime(stake.end_sec);

  return (
    <div
      className={cn(
        "bg-bg-secondary rounded-sm px-4 py-3 space-y-4 transition-colors border",
        isExpanded ? "border-accent-secondary" : "border-stroke-light",
        stake.is_matured && !isExpanded && "border-bg-primary",
      )}
    >
      <Collapsible
        dir="rtl"
        open={isExpanded}
        onOpenChange={setIsExpanded}
        className="space-y-2"
      >
        {/* Row 1: Plan type + ID | Dates */}
        <div className="flex justify-between w-full text-xs">
          <div className="space-y-2">
            <p>
              <span className="font-bold">{t("card.planTypeLabel")}</span>
              <span>{stake.plan}</span>
            </p>
            <p>
              <span className="font-bold">{t("card.idLabel")}</span>
              <span>{stake.stake_id}</span>
            </p>
          </div>
          <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-2 text-text-secondary items-center">
            <span>{t("card.startDate")}</span>
            <span className="text-sm" dir="ltr">
              {startDate}
            </span>
            <span>{t("card.endDate")}</span>
            <span className="text-sm" dir="ltr">
              {endDate}
            </span>
          </div>
        </div>

        {/* Row 2: Claimable amount + claim button */}
        <div className="flex items-center justify-between w-full text-xs pt-1">
          <p className="flex items-center gap-1">
            <span>{t("card.claimable")}</span>
            <span dir="ltr">
              {formatStakeNumber(stake.drip_net_uvm_wei)} UVM
            </span>
            {stake.drip_net_uvm_wei > 0n && (
              <InfoTooltip iconSize={14}>{t("card.feeNote")}</InfoTooltip>
            )}
          </p>
          <ClaimRewardButton stake={stake} />
        </div>

        <CollapsibleContent className="text-xs pt-5 space-y-4 text-text-secondary">
          {/* Duration + DNM + UVM */}
          <div className="flex items-center justify-between">
            <p>{durationLabel(stake.duration, t)}</p>
            <Separator orientation="vertical" className="bg-stroke-light h-5" />
            <p dir="ltr">{formatStakeNumber(stake.dao)} DNM</p>
            <Separator orientation="vertical" className="bg-stroke-light h-5" />
            <p dir="ltr">{formatStakeNumber(stake.uvm)} UVM</p>
          </div>
          {/* Total reward + remaining reward */}
          <div className="flex items-center justify-between">
            <p>
              <span>{t("card.totalReward")}</span>
              <span dir="ltr">
                {formatStakeNumber(stake.total_reward, { maxDecimals: 2 })} UVM
              </span>
            </p>
            <p>
              <span>{t("card.remainingReward")}</span>
              <span dir="ltr">
                {formatStakeNumber(
                  stake.reward === 0 ? stake.total_reward : stake.reward,
                  { maxDecimals: 2 },
                )}{" "}
                UVM
              </span>
            </p>
          </div>
          {/* Withdraw stake button */}
          <div className="flex items-center justify-end">
            <WithdrawStakeButton stake={stake} />
          </div>
        </CollapsibleContent>

        <CollapsibleTrigger
          className="w-full text-xs data-[state=open]:pt-3 flex items-center justify-center gap-x-2"
          openedText={tCommon("viewLess")}
          closedText={tCommon("viewMore")}
        />
      </Collapsible>
    </div>
  );
};
